import { Tooltip } from '@chakra-ui/react'
import ApplyV2AttachmentsUploader from '@core/components/ApplyDialogV2/ApplyV2AttachmentsUploader'
import ApplyV2Button from '@core/components/ApplyDialogV2/ApplyV2Button'
import ApplyV2InputCheckbox from '@core/components/ApplyDialogV2/ApplyV2InputCheckbox'
import { FILE_TYPES } from '@core/utils/constants/constants'
import getAcceptFilesString from '@core/utils/helpers/getAcceptFilesString'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import ApplyV2InputText from '@core/components/ApplyDialogV2/ApplyV2InputText'
import ApplyV2Textarea from '@core/components/ApplyDialogV2/ApplyV2Textarea'
import ApplyV2InputTextarea from '@core/components/ApplyDialogV2/ApplyV2InputTextarea'
import ApplyV2Switch from '@core/components/ApplyDialogV2/ApplyV2Switch'
import ApplyV2DateSelectGroup from '@core/components/ApplyDialogV2/ApplyV2DateSelectGroup'
import ApplyV2Select from '@core/components/ApplyDialogV2/ApplyV2Select'
import FileUploaderV2 from '@core/components/ApplyDialogV2/FileUploaderV2'
import FileUploaderV2List from '@core/components/ApplyDialogV2/FileUploaderV2List'
import {
    usePostCourseFileV2ApplyMutation,
    usePostCourseV2ApplyMutation,
    usePutCourseV2ApplyMutation,
    useDeleteCourseFileV2ApplyMutation,
} from '@core/app/api/applyJobV2Api'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import type {
    ApplyV2FileAttachment,
    ApplyV2TempBase64File,
    ApplyV2TempCourse,
    ApplyV2TempCourseFile,
    V2CourseFile,
} from '@core/app/types/applyV2Type'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import React, { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import './ApplyV2CoursesStepEdit.scss'

interface Props {
    className?: string
    onClose?: () => void
    item: ApplyV2TempCourse
}

const baseClass = 'ApplyV2CoursesStepEdit'

function ApplyV2CoursesStepEdit({ className, onClose = () => { }, item }: Props) {
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const {
        postProfileData,
        postProfileError,
        isPostProfileLoading,
        isPostProfileSuccess,
        isSettingsLoading,
        isSettingsFetching,
        isFinalStep,
        setIsFinalStep,
        isPostApplied,
        onCloseApply,
        settingsData,
        navItems,
        storageData,
        postProfileRefetch,
    } = useApplyV2Context()
    const [postFileRequest, { isLoading: isFileLoading, error: fileError, data: fileData }] =
        usePostCourseFileV2ApplyMutation()
    const [deleteFileRequest, { isLoading: isDeleteFileLoading, error: deleteFileError, data: deleteFileData }] =
        useDeleteCourseFileV2ApplyMutation()
    const [postCourseRequest, { isLoading: isCourseLoading, error: courseErrors, data: courseData }] =
        usePutCourseV2ApplyMutation()

    // const [loadingFileId, setLoadingFileId] = React.useState<string | null>(null)
    const [fileUploadingCount, setFileUploadingCount] = React.useState<number>(0)
    const [deletingFilesIds, setDeletingFilesIds] = React.useState<(string | number)[]>([])
    const firstRef = useRef<any>(null)

    useEffect(() => {
        if (firstRef.current) {
            firstRef.current.focus()
        }
    }, [])

    const isExpireDateMandatory = !!settingsData?.corporationSettings?.courseExpiryMandatory
    const validationSchema = Yup.object().shape({
        courseName: Yup.string()
            .trim()
            .required(`${getTitle('5653', 'Required')}`)
            .min(2, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`)
            .max(255, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 255 })}`),
        courseDescription: Yup.string()
            .trim()
            .required(getTitle('5653', 'Required') ?? '')
            .min(2, `${t('8336', 'The text length should be a minimum of {{value}} characters.', { value: 2 })}`)
            .max(5000, `${t('8337', 'The text length should be a maximum of {{value}} characters.', { value: 5000 })}`),
        issueDate: Yup.boolean(),
        expireDate: Yup.boolean(),
        noExpireDate: Yup.boolean(),
        startDateDay: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string()
                .nullable()
                .required(getTitle('5653', 'Required'))
                .test('valid-date', getTitle('148', 'Invalid date'), function (value) {
                    const { startDateYear, startDateMonth, startDateDay } = this.parent
                    if (!startDateDay || !startDateMonth || !startDateYear) return true

                    const birthDate = new Date(Number(startDateYear), Number(startDateMonth) - 1, Number(startDateDay))
                    const isValid =
                        birthDate.getFullYear() === Number(startDateYear) &&
                        birthDate.getMonth() === Number(startDateMonth) - 1 &&
                        birthDate.getDate() === Number(startDateDay)

                    return isValid
                }),
            otherwise: Yup.string().nullable(),
        }),
        startDateMonth: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string().nullable().required(getTitle('5653', 'Required')),
            otherwise: Yup.string().nullable(),
        }),
        startDateYear: Yup.string().when(['issueDate'], {
            is: true,
            then: Yup.string().nullable().required(getTitle('5653', 'Required')),
            otherwise: Yup.string().nullable(),
        }),
        endDateDay: Yup.string().when(['expireDate', 'noExpireDate'], {
            // is: true,
            is: (expireDate: boolean, noExpireDate: boolean) => {
                return expireDate && !noExpireDate
            },
            then: Yup.string()
                .required(getTitle('5653', 'Required'))
                .test(
                    'is-after-start',
                    getTitle('9453', 'End date cannot be earlier than start date'),
                    function (value) {
                        const { startDateYear, startDateMonth, startDateDay, endDateDay, endDateMonth, endDateYear } =
                            this.parent
                        if (!isExpireDateMandatory) return true
                        if (!startDateDay || !startDateMonth || !startDateYear) return true

                        const startDate = dayjs(`${startDateYear}-${startDateMonth}-${startDateDay}`)
                        const endDate = dayjs(`${endDateYear}-${endDateMonth}-${endDateDay}`)

                        return endDate.isAfter(startDate) || endDate.isSame(startDate, 'day')
                    }
                )
                .test('valid-date', getTitle('148', 'Invalid date'), function (value) {
                    const { endDateYear, endDateMonth, endDateDay } = this.parent
                    if (!endDateDay || !endDateMonth || !endDateYear) return true

                    const birthDate = new Date(Number(endDateYear), Number(endDateMonth) - 1, Number(endDateDay))
                    const isValid =
                        birthDate.getFullYear() === Number(endDateYear) &&
                        birthDate.getMonth() === Number(endDateMonth) - 1 &&
                        birthDate.getDate() === Number(endDateDay)

                    return isValid
                }),
            // .required(t('5653', 'Required') ?? ''),
            otherwise: Yup.string().nullable(),
        }),
        endDateMonth: Yup.string()
            .nullable()
            .when(['expireDate', 'noExpireDate'], {
                is: (expireDate: boolean, noExpireDate: boolean) => {
                    return expireDate && !noExpireDate
                },
                then: Yup.string()
                    .required(getTitle('5653', 'Required'))
                    .test(
                        'is-after-start',
                        getTitle('9453', 'End date cannot be earlier than start date'),
                        function (value) {
                            const {
                                startDateYear,
                                startDateMonth,
                                startDateDay,
                                endDateDay,
                                endDateMonth,
                                endDateYear,
                            } = this.parent
                            if (!isExpireDateMandatory) return true
                            if (!startDateDay || !startDateMonth || !startDateYear) return true

                            const startDate = dayjs(`${startDateYear}-${startDateMonth}-${startDateDay}`)
                            const endDate = dayjs(`${endDateYear}-${endDateMonth}-${endDateDay}`)

                            return endDate.isAfter(startDate) || endDate.isSame(startDate, 'day')
                        }
                    ),
                otherwise: Yup.string().nullable(),
            }),
        endDateYear: Yup.string()
            .nullable()
            .when(['expireDate', 'noExpireDate'], {
                is: (expireDate: boolean, noExpireDate: boolean) => {
                    return expireDate && !noExpireDate
                },
                then: Yup.string()
                    .required(getTitle('5653', 'Required'))
                    .test(
                        'is-after-start',
                        getTitle('9453', 'End date cannot be earlier than start date'),
                        function (value) {
                            const {
                                startDateYear,
                                startDateMonth,
                                startDateDay,
                                endDateDay,
                                endDateMonth,
                                endDateYear,
                            } = this.parent
                            if (!isExpireDateMandatory) return true
                            if (!startDateDay || !startDateMonth || !startDateYear) return true

                            const startDate = dayjs(`${startDateYear}-${startDateMonth}-${startDateDay}`)
                            const endDate = dayjs(`${endDateYear}-${endDateMonth}-${endDateDay}`)

                            return endDate.isAfter(startDate) || endDate.isSame(startDate, 'day')
                        }
                    ),
                otherwise: Yup.string().nullable(),
            }),
        files: Yup.array()
            .max(5)
            .of(
                Yup.object().shape({
                    uid: Yup.string().nullable().optional(),
                    base64: Yup.string().nullable().optional(),
                    name: Yup.string().required(),
                    size: Yup.number()
                        .required()
                        .max(
                            10 * 1024 * 1024,
                            t('10531', 'File size cannot exceed {{sizeMb}} MB', {
                                sizeMb: 10,
                            }) ?? ''
                        ),
                    ext: Yup.string()
                        .required()
                        .oneOf(FILE_TYPES.ATTACHMENTS.EXT, getTitle('10532', 'Invalid file format')),
                    lastModified: Yup.number().optional(),
                    uploaded: Yup.string().optional(),
                })
            ),
    })

    const startDateDay = item.startDate ? new Date(item.startDate).getDate().toString().padStart(2, '0') : ''
    const startDateMonth = item.startDate ? (new Date(item.startDate).getMonth() + 1).toString().padStart(2, '0') : ''
    const startDateYear = item.startDate ? new Date(item.startDate).getFullYear().toString() : ''
    const endDateDay = item.endDate ? new Date(item.endDate).getDate().toString().padStart(2, '0') : ''
    const endDateMonth = item.endDate ? (new Date(item.endDate).getMonth() + 1).toString().padStart(2, '0') : ''
    const endDateYear = item.endDate ? new Date(item.endDate).getFullYear().toString() : ''

    const formik = useFormik({
        initialValues: {
            courseName: item.courseName ?? '',
            courseDescription: item.courseDescription ?? '',
            issueDate: !!item.startDate,
            expireDate: isExpireDateMandatory,
            noExpireDate: !item.endDate,
            startDateDay: startDateDay,
            startDateMonth: startDateMonth,
            startDateYear: startDateYear,
            endDateDay: endDateDay,
            endDateMonth: endDateMonth,
            endDateYear: endDateYear,
            files: item.files || [],
        },
        onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
            try {
                const uid = storageData?.postInitUid

                if (!uid) return
                setSubmitting(true)
                const startDate = values.issueDate
                    ? `${values.startDateYear}-${values.startDateMonth}-${values.startDateDay}`
                    : null
                const endDate =
                    values.expireDate &&
                        !values.noExpireDate &&
                        values.endDateYear &&
                        values.endDateMonth &&
                        values.endDateYear
                        ? `${values.endDateYear}-${values.endDateMonth}-${values.endDateDay}`
                        : null

                const filesForDelete = (item.files ?? []).filter((f) => {
                    const isExistInValues = formik.values.files.some((fi) => f.uid === fi.uid)
                    return !isExistInValues
                })

                const filesForUpload = (formik.values.files ?? []).filter((f) => {
                    const isExistInValues = (item.files ?? []).some((fi) => f.uid === fi.uid || f.name === fi.name)
                    return !isExistInValues
                })

                const deleteSequentially = async () => {
                    for (const fileDelete of filesForDelete) {
                        try {
                            const res = await deleteFileRequest({
                                uid: uid,
                                courseUid: item.uid,
                                fileUid: fileDelete.uid,
                            }).unwrap()
                        } catch (e: any) {
                            formik.setFieldError('files', e?.message || 'error')
                        }
                    }
                }

                const uploadSequentially = async () => {
                    const errs = []
                    for (const fileUpload of filesForUpload) {
                        try {
                            if ('base64' in fileUpload && typeof fileUpload.base64 === 'string') {
                                const res = await postFileRequest({
                                    uid: uid,
                                    body: {
                                        uid: item.uid,
                                        file: {
                                            name: fileUpload.name,
                                            ext: fileUpload.ext || fileUpload.name.split('.').pop() || '',
                                            base64: fileUpload.base64,
                                        },
                                    },
                                }).unwrap()
                            }
                        } catch (e: any) {
                            errs.push(e)
                            formik.setFieldError('files', e?.message || 'error')
                        }
                    }
                    return errs
                }

                const del = await deleteSequentially()
                const uploads = await uploadSequentially()

                const res = await postCourseRequest({
                    uid,
                    body: {
                        uid: item.uid,
                        courseName: values.courseName,
                        courseDescription: values.courseDescription,
                        startDate,
                        endDate,
                        courseListId: 0,
                    },
                }).unwrap()
                await postProfileRefetch()
                onClose()
            } catch (error: any) {
                console.error('Submission error:', error)
                if (error?.data?.errors && Array.isArray(error.data.errors)) {
                    const formikErrors: { [key: string]: string } = {}

                    error.data.errors.forEach((err: any) => {
                        switch (err.field) {
                            case 'fromDate':
                                formikErrors.startYear = err.msg
                                formikErrors.startMonth = err.msg
                                break
                            case 'toDate':
                                formikErrors.endYear = err.msg
                                formikErrors.endMonth = err.msg
                                break
                            case 'companyName':
                            case 'title':
                            case 'location':
                            case 'description':
                                formikErrors[err.field] = err.msg
                                break
                            default:
                                formikErrors.root = err.msg
                                break
                        }
                    })

                    setErrors(formikErrors)
                }
            } finally {
                setSubmitting(false)
                console.log('Form submission completed')
            }
        },
        validationSchema,
        // validateOnMount: false,
        // validateOnBlur: true,
        validateOnChange: true,
        enableReinitialize: true,
    })

    const handleFileDelete = async (
        file: ApplyV2TempCourseFile | File | ApplyV2FileAttachment | V2CourseFile | ApplyV2TempBase64File
    ) => {
        const uid = storageData?.postInitUid || ''
        if (!uid) return
        try {
            const fileUid = (file as ApplyV2TempCourseFile).uid
            setDeletingFilesIds((prev) => {
                return [...new Set([...prev, fileUid])]
            })
            // setLoadingFileId(fileUid)
            if (!fileUid) return
            const res = await deleteFileRequest({
                uid,
                courseUid: item.uid,
                fileUid,
            }).unwrap()
            console.log('File delete response:', res)
            // const updatedFiles = formik.values.files.filter((f) => f.uid !== fileUid)
            // await formik.setFieldValue('files', updatedFiles)
            await postProfileRefetch()
        } catch (error) {
            console.error('File delete error:', error)
            // Optionally, you can show an error message to the user
            if (deleteFileError) {
                console.error('Delete file error:', deleteFileError)
            }
        } finally {
            // setLoadingFileId(null)
            setDeletingFilesIds((prev) => {
                return prev.filter((file) => file !== uid)
            })
        }
    }

    return (
        <form className={clsx(baseClass, className)} onSubmit={formik.handleSubmit} noValidate>
            <div className={`${baseClass}__header`}>
                <div className={`${baseClass}__header__title`}>{getTitle('10389', 'Edit course / certificate')}</div>
                <Tooltip
                    hasArrow
                    // isDisabled={isLoading}
                    openDelay={500}
                    label={getTitle('181', 'Close')}
                    // placement={'right'}
                    padding={'8px 16px'}
                    borderRadius={'4px'}
                    backgroundColor={'#19191A'}
                    fontWeight={600}
                    fontSize={'12px'}
                    shouldWrapChildren
                >
                    <ApplyV2Button
                        className={`${baseClass}__header__btn`}
                        size={'small'}
                        variant="transparent"
                        onClick={onClose}
                        leftIcon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                    fill="#19191A"
                                />
                            </svg>
                        }
                    />
                </Tooltip>
            </div>
            <ApplyV2InputText
                ref={firstRef}
                name="courseName"
                label={getTitle('63', 'Name')}
                value={formik.values.courseName}
                onChange={(e) => {
                    // formik.setFieldTouched('courseName', true)
                    formik.handleChange(e)
                }}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.courseName && formik.errors.courseName}
                // errorMessage={formik.errors.courseName}
                isRequired
            />
            <div className={`${baseClass}__dates`}>
                <ApplyV2Switch
                    name="issueDate"
                    checked={formik.values.issueDate}
                    // onChange={formik.handleChange}
                    onChange={(e) => {
                        formik
                            .setValues({
                                ...formik.values,
                                issueDate: e.target.checked,
                                startDateDay: '',
                                startDateMonth: '',
                                startDateYear: '',
                            })
                            .then((l) => {
                                formik.setErrors({ ...l })
                                formik.setTouched({
                                    ...formik.touched,
                                    issueDate: true,
                                    startDateYear: true,
                                    startDateMonth: true,
                                    startDateDay: true,
                                })
                            })
                    }}
                    onBlur={formik.handleBlur}
                    label={getTitle('7865', 'Issue date')}
                />

                {formik.values.issueDate && (
                    <ApplyV2DateSelectGroup
                        // label={getTitle('7577', 'Expiry date')}
                        // isRequired
                        names={{
                            day: 'startDateDay',
                            month: 'startDateMonth',
                            year: 'startDateYear',
                        }}
                        value={{
                            day: formik.values.startDateDay,
                            month: formik.values.startDateMonth,
                            year: formik.values.startDateYear,
                        }}
                        onChange={({ day, month, year }) => {
                            formik.setFieldValue('startDateDay', day)
                            formik.setFieldValue('startDateMonth', month)
                            formik.setFieldValue('startDateYear', year)
                        }}
                        errors={{
                            day: formik.touched.startDateDay && formik.errors.startDateDay,
                            month: formik.touched.startDateMonth && formik.errors.startDateMonth,
                            year: formik.touched.startDateYear && formik.errors.startDateYear,
                        }}
                        onBlur={formik.handleBlur}
                        isDisabled={!formik.values.issueDate}
                        isFutureYears={false}
                        yearsRange={90}
                        isCurrentYear={true}
                    />
                )}
                {!isExpireDateMandatory && (
                    <ApplyV2Switch
                        name="expireDate"
                        checked={formik.values.expireDate}
                        // onChange={formik.handleChange}
                        onChange={(e) => {
                            formik
                                .setValues({
                                    ...formik.values,
                                    expireDate: e.target.checked,
                                    endDateDay: '',
                                    endDateMonth: '',
                                    endDateYear: '',
                                })
                                .then((errs) => {
                                    formik.setErrors({ ...errs })
                                    formik.setTouched({
                                        ...formik.touched,
                                        expireDate: e.target.checked,
                                        endDateDay: true,
                                        endDateMonth: true,
                                        endDateYear: true,
                                    })
                                })
                        }}
                        onBlur={formik.handleBlur}
                        label={getTitle('7577', 'Expiry date')}
                    />
                )}
                {formik.values.expireDate && (
                    <ApplyV2DateSelectGroup
                        label={getTitle('7577', 'Expiry date')}
                        topBar={
                            isExpireDateMandatory && (
                                <ApplyV2InputCheckbox
                                    className={`${baseClass}__noExpire`}
                                    value={formik.values.noExpireDate}
                                    onChange={(e) => {
                                        formik.setFieldValue('noExpireDate', e)
                                        if (e) {
                                            formik
                                                .setValues({
                                                    ...formik.values,
                                                    noExpireDate: e,
                                                    endDateDay: '',
                                                    endDateMonth: '',
                                                    endDateYear: '',
                                                })
                                                .then((errs) => {
                                                    formik.setErrors({ ...errs })
                                                    formik
                                                        .setTouched({
                                                            ...formik.touched,
                                                            endDateDay: undefined,
                                                            endDateMonth: undefined,
                                                            endDateYear: undefined,
                                                        })
                                                        .then((s) => { })
                                                })
                                        }
                                    }}
                                    label={getTitle('9527', 'Does not have an expiry date')}
                                    onBlur={formik.handleBlur}
                                />
                            )
                        }
                        // isRequired
                        names={{
                            day: 'endDateDay',
                            month: 'endDateMonth',
                            year: 'endDateYear',
                        }}
                        value={{
                            day: formik.values.endDateDay,
                            month: formik.values.endDateMonth,
                            year: formik.values.endDateYear,
                        }}
                        onChange={({ day, month, year }) => {
                            formik.setFieldValue('endDateDay', day)
                            formik.setFieldValue('endDateMonth', month)
                            formik.setFieldValue('endDateYear', year)
                        }}
                        errors={{
                            day: formik.touched.endDateDay && formik.errors.endDateDay,
                            month: formik.touched.endDateMonth && formik.errors.endDateMonth,
                            year: formik.touched.endDateYear && formik.errors.endDateYear,
                        }}
                        onBlur={formik.handleBlur}
                        isDisabled={!formik.values.expireDate || formik.values.noExpireDate}
                        isRequired={isExpireDateMandatory}
                        isFutureYears={!!formik.values.startDateYear}
                        yearsRange={!!formik.values.startDateYear ? 0 : 80}
                        yearsSart={formik.values.startDateYear}
                        isCurrentYear={false}
                    />
                )}
            </div>

            <ApplyV2Textarea
                name={'courseDescription'}
                label={getTitle('606', 'Description')}
                // isLoading={true}
                value={formik.values.courseDescription}
                // placeholder={getTitle('7606', 'Description text')}
                onChange={(v) => {
                    formik.setFieldValue('courseDescription', v)
                }}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.courseDescription && formik.errors.courseDescription}
                isRequired
            // description={'daw awd'}
            />
            {/*<ApplyV2InputTextarea*/}
            {/*    name="courseDescription"*/}
            {/*    label={getTitle('606', 'Description')}*/}
            {/*    value={formik.values.courseDescription}*/}
            {/*    onChange={(e) => {*/}
            {/*        // formik.setFieldTouched('courseDescription', true)*/}
            {/*        formik.handleChange(e)*/}
            {/*    }}*/}
            {/*    onBlur={formik.handleBlur}*/}
            {/*    errorMessage={formik.touched.courseDescription && formik.errors.courseDescription}*/}
            {/*    isRequired*/}
            {/*/>*/}
            <ApplyV2AttachmentsUploader
                // className={`${baseClass}__files`}
                label={getTitle('1044', 'Attachments')}
                name="files"
                // isRequired
                multipleCount={5}
                // isDisabled={true}
                maxFileSize={10}
                files={formik.values.files}
                uploadingCount={fileUploadingCount}
                // accept={['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png']}
                accept={getAcceptFilesString('ATTACHMENTS')}
                // onChange={handleFileChangeHandler}
                onChange={(files: ApplyV2TempBase64File[]) => {
                    formik.setFieldTouched('files', true)
                    const filesMap = new Map<string, ApplyV2TempBase64File | ApplyV2TempCourseFile>()
                    formik.values.files?.forEach((file: ApplyV2TempBase64File | ApplyV2TempCourseFile) => {
                        if (file?.name) {
                            filesMap.set(file.name, file)
                        }
                    })
                    files.forEach((file) => {
                        if (file?.name) {
                            filesMap.set(file.name, file)
                        }
                    })
                    const uniqueFiles = Array.from(filesMap.values())
                    formik.setFieldValue('files', uniqueFiles).then((errs) => {
                        // formik.setFieldTouched('files', true)
                    })
                }}
                onBlur={formik.handleBlur}
                errorMessage={formik.touched.files && formik.errors.files}
                // loadingIds={deletingFilesIds}
                // onDelete={handleFileDelete}
                onDelete={(file) => {
                    formik
                        .setFieldValue(
                            'files',
                            formik.values.files.filter(
                                (f: File | ApplyV2TempCourseFile | ApplyV2TempBase64File) => f.name !== file.name
                            )
                        )
                        .then((errs) => {
                            formik.setFieldTouched('files', true)
                        })
                }}
            />
            <div className={`${baseClass}__btns`}>
                <ApplyV2Button
                    type={'reset'}
                    variant="transparent"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    // isLoading={formik.isSubmitting}
                    onClick={() => {
                        formik.resetForm()
                        onClose()
                    }}
                >
                    {getTitle('416', 'Cancel')}
                </ApplyV2Button>
                <ApplyV2Button
                    type={'submit'}
                    variant="primary"
                    className={`${baseClass}__btns--btn ${baseClass}__btns--btn--submit`}
                    isLoading={formik.isSubmitting}
                    isDisabled={formik.isSubmitting || !formik.dirty}
                // isDisabled={!formik.isValid || !formik.dirty}
                // onClick={() => {
                //     console.log('Submit clicked', formik.values)
                //     console.log('Formik state:', formik.touched)
                //     console.log('Formik errors:', formik.errors)
                // }}
                >
                    {getTitle('12', 'Save')}
                </ApplyV2Button>
            </div>
        </form>
    )
}

export default ApplyV2CoursesStepEdit
